'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from 'react';
import { useAppLocale } from '@/hooks';
import { Link } from '@/i18n/navigation';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { Autoplay, EffectFade, Keyboard, Mousewheel, Navigation, Pagination } from 'swiper/modules';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { LoadingIndicator } from '@/components/partials';
import { SlidesListResponse } from '@/types/slide';
import { MediaTypeEnum } from '@/enums';
import { getResizedPictureUrl, getVideoUrl, getLocalizedValue } from '@/lib/utils';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
type PropsType = {
  items: SlidesListResponse;
};

/**
 * Convert a YouTube URL (watch, youtu.be or embed form) into an autoplaying, muted,
 * looping background embed URL suitable for a hero slide.
 */
const toYouTubeEmbed = (url: string): string => {
  const id = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|v\/))([\w-]{11})/)?.[1];
  if (!id) {
    return url;
  }
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    loop: '1',
    playlist: id,
    controls: '0',
    showinfo: '0',
    modestbranding: '1',
    rel: '0',
    playsinline: '1'
  });
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
};

/**
 * Render a slide title. The part after a "|" separator is highlighted with the brand (yellow) accent color.
 * e.g. "SHIFT TO HIGHER GEAR | WITH BUMEKS" -> base white + "WITH BUMEKS" in brand.
 */
const renderTitle = (title: string) => {
  const separatorIndex = title.indexOf('|');
  if (separatorIndex === -1) {
    return title;
  }
  const base = title.slice(0, separatorIndex).trim();
  const accent = title.slice(separatorIndex + 1).trim();
  return <>
      {base}
      {accent && <span className="text-brand"> {accent}</span>}
    </>;
};

/**
 * Coordinated entrance for the slide text. Every line uses the same motion (a gentle
 * upward fade) and is revealed with a smooth, even stagger — so nothing flies in from
 * different directions at different times.
 */
const contentContainer: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.08
    }
  },
  exit: {
    transition: {
      staggerChildren: 0.06,
      staggerDirection: -1
    }
  }
};
const contentItem: Variants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: [0.23, 1, 0.32, 1]
    }
  },
  exit: {
    opacity: 0,
    y: -16,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  }
};
const HomeSliderSwiper = t0 => {
  const $ = _c(35);
  const {
    items
  } = t0;
  const {
    backendLocale
  } = useAppLocale();
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [speed, setSpeed] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [loadedImages, setLoadedImages] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== swiperInstance) {
    t2 = () => {
      setIsMounted(true);
      if (swiperInstance) {
        setTimeout(() => {
          setSpeed(300);
          swiperInstance.autoplay.start();
        }, 100);
      }
    };
    t3 = [swiperInstance];
    $[1] = swiperInstance;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== activeIndex || $[5] !== items || $[6] !== loadedImages) {
    t4 = () => {
      if (items && items.length > 0) {
        const nextIndex = (activeIndex + 1) % items.length;
        if (!loadedImages.has(nextIndex) && items[nextIndex]?.picture?.path) {
          const img = new window.Image();
          img.src = getResizedPictureUrl(items[nextIndex].picture.path, 2040, 1148);
        }
      }
    };
    t5 = [activeIndex, items, loadedImages];
    $[4] = activeIndex;
    $[5] = items;
    $[6] = loadedImages;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = index => {
      setLoadedImages(prev => {
        const newSet = new Set(prev);
        newSet.add(index);
        return newSet;
      });
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleImageLoad = t6;
  const t7 = `home-slider relative -mt-16 select-none md:-mt-34 ${!isMounted ? "first-load" : ""}`;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      crossFade: true
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      delay: 5000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      clickable: true
    };
    t11 = {
      nextEl: ".arrow-left",
      prevEl: ".arrow-right"
    };
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [Autoplay, EffectFade, Pagination, Navigation, Mousewheel, Keyboard];
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = swiper => setSwiperInstance(swiper);
    t14 = swiper_0 => setActiveIndex(swiper_0.realIndex);
    $[15] = t13;
    $[16] = t14;
  } else {
    t13 = $[15];
    t14 = $[16];
  }
  let t15;
  if ($[17] !== activeIndex || $[18] !== backendLocale || $[19] !== items || $[20] !== loadedImages) {
    let t16;
    if ($[22] !== activeIndex || $[23] !== backendLocale || $[24] !== loadedImages) {
      t16 = (slide, key) => {
        const hasMedia = Boolean(slide.mediaType === MediaTypeEnum.VIDEO && slide.video || slide.mediaType === MediaTypeEnum.YOUTUBE && slide.videoUrl || slide.picture);
        const isVideoSlide = slide.mediaType === MediaTypeEnum.VIDEO && Boolean(slide.video);
        const isContentReady = !hasMedia || isVideoSlide || loadedImages.has(key);
        return <SwiperSlide key={key}><div className="relative h-full w-full">{!loadedImages.has(key) && !isVideoSlide && <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-200"><LoadingIndicator size="small" /></div>}{isVideoSlide ? <>{slide.picture && <Image src={getResizedPictureUrl(slide.picture.path, 2040, 1148)} alt={getLocalizedValue(slide, "title", backendLocale)} fill={true} sizes="100vw" className="object-cover" loading={key === 0 ? "eager" : "lazy"} priority={key === 0} />}<video ref={_temp2} className="absolute inset-0 h-full w-full object-cover" src={getVideoUrl(slide.video.path)} autoPlay={true} muted={true} loop={true} playsInline={true} preload="auto" /></> : slide.mediaType === MediaTypeEnum.YOUTUBE && slide.videoUrl ? <div className="pointer-events-none absolute inset-0 overflow-hidden"><iframe className="absolute top-1/2 left-1/2 h-[56.25vw] min-h-full w-full -translate-x-1/2 -translate-y-1/2 border-0" src={toYouTubeEmbed(slide.videoUrl)} title={getLocalizedValue(slide, "title", backendLocale) || "video"} allow="autoplay; encrypted-media; picture-in-picture" onLoad={() => handleImageLoad(key)} /></div> : slide.picture ? <Image src={getResizedPictureUrl(slide.picture.path, 2040, 1148)} alt={getLocalizedValue(slide, "title", backendLocale)} fill={true} sizes="100vw" className="object-cover" loading={key === 0 ? "eager" : "lazy"} priority={key === 0} onLoad={() => handleImageLoad(key)} /> : null}<div className="absolute inset-0 bg-linear-to-br from-black/60 via-transparent to-black/40" /><div className="absolute inset-0 z-10 flex items-center pt-16 md:pt-34"><div className="w-full px-8 transition-all duration-300 md:px-16 lg:px-24"><AnimatePresence mode="wait">{activeIndex === key && isContentReady && (() => {
                    const title = getLocalizedValue(slide, "title", backendLocale);
                    const content = getLocalizedValue(slide, "content", backendLocale);
                    const url = getLocalizedValue(slide, "url", backendLocale);
                    const hasTitle = title && title !== "-";
                    const hasContent = content && content !== "-";
                    const hasUrl = url && url !== "-";
                    if (!hasTitle && !hasContent && !hasUrl) {
                      return null;
                    }
                    return <motion.div key={`content-${key}`} variants={contentContainer} initial="hidden" animate="show" exit="exit" className="relative w-full max-w-md text-left transition-[max-width] duration-300 md:max-w-lg lg:max-w-xl">{hasTitle && <motion.h2 variants={contentItem} className="mb-2 text-2xl leading-8 font-bold text-white transition-[font-size,line-height] duration-300 [-webkit-text-stroke:1.5px_rgba(0,0,0,0.25)] [paint-order:stroke_fill] md:text-3xl md:leading-7 lg:text-7xl lg:leading-16">{renderTitle(title)}</motion.h2>}{hasContent && <motion.p variants={contentItem} className="text-md mb-6 max-w-2xl leading-tight font-light text-white transition-[font-size,line-height] duration-300 [-webkit-text-stroke:1px_rgba(0,0,0,0.2)] [paint-order:stroke_fill] md:text-xl lg:max-w-none lg:whitespace-nowrap">{content}</motion.p>}{hasUrl && <motion.div variants={contentItem} className="flex flex-wrap gap-2"><Link href={url} className="group bg-brand shadow-brand/50 hover:shadow-brand/60 relative inline-flex items-center gap-2 overflow-hidden rounded-lg px-6 py-3 font-semibold text-black shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"><span className="relative z-10 uppercase">Details</span><ArrowRightIcon className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /><div className="from-brand-darken to-brand absolute inset-0 z-0 bg-linear-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100" /></Link></motion.div>}</motion.div>;
                  })()}</AnimatePresence></div></div></div></SwiperSlide>;
      };
      $[22] = activeIndex;
      $[23] = backendLocale;
      $[24] = loadedImages;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    t15 = items.map(t16);
    $[17] = activeIndex;
    $[18] = backendLocale;
    $[19] = items;
    $[20] = loadedImages;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[26] !== speed || $[27] !== t15) {
    t16 = <Swiper spaceBetween={15} slidesPerView={1} centeredSlides={true} speed={speed} effect="fade" fadeEffect={t8} watchSlidesProgress={true} autoplay={t9} rewind={true} pagination={t10} navigation={t11} mousewheel={false} keyboard={true} modules={t12} className="z-0 h-lvh" onSwiper={t13} onSlideChange={t14}>{t15}</Swiper>;
    $[26] = speed;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== items.length) {
    t17 = items.length > 1 && <><button className="arrow-left arrow"><ArrowRightIcon /></button><button className="arrow-right arrow"><ArrowLeftIcon /></button></>;
    $[29] = items.length;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t16 || $[32] !== t17 || $[33] !== t7) {
    t18 = <div className={t7}>{t16}{t17}</div>;
    $[31] = t16;
    $[32] = t17;
    $[33] = t7;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  return t18;
};
export default HomeSliderSwiper;
function _temp() {}
function _temp2(el) {
  if (el) {
    el.muted = true;
    el.play().catch(_temp);
  }
}